import { EvidenceTypeEnum, type EvidenceUploadMode, useApiEvidenceV1BulkUpdateSiteMaterialPartialUpdate } from 'api';
import { EvidenceTypeSelector, Form, type InferType, useForm, yup, yupResolver } from 'form';
import { type I18nScopedKey, type I18nTranslateFunction, useTranslation } from 'i18n';
import { ConfirmationButton, Stack } from 'ui';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';

import { TradeCompanySiteMaterialPicker } from '../../../site-material';
import { useBulkDeleteEvidence } from '../../hooks/useBulkDeleteEvidence/useBulkDeleteEvidence';

type TranslationFunction = I18nTranslateFunction<I18nScopedKey<'features'>>;

const buildSchema = (t: TranslationFunction) =>
  yup
    .object()
    .shape({
      evidence_type: yup.string().oneOf(Object.values(EvidenceTypeEnum)),
      trade_company: yup.string(),
      trade_company_site_material: yup.string(),
    })
    .test(
      'evidence_type-or-trade_company_site_material',
      t('evidence.editForm.validation.typeOrSiteMaterialRequired'),
      function (values) {
        if (!values.evidence_type && !values.trade_company_site_material) {
          return this.createError({
            message: t('evidence.editForm.validation.typeOrSiteMaterialRequired'),
            path: 'evidence_type',
          });
        }
        return true;
      }
    );

type FormValues = InferType<ReturnType<typeof buildSchema>>;
export const EvidenceEditForm = ({
  deleteMode,
  deleteParentId,
  selectedEvidenceIds,
  onSuccess,
}: {
  deleteMode: EvidenceUploadMode;
  deleteParentId: number | string;
  selectedEvidenceIds: number[];
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation('features');
  const { t: tCommon } = useTranslation('common');
  const { open } = useNotification();
  const evidenceLabel = tCommon('domains.evidence');
  const schema = buildSchema(t);

  const form = useForm({
    resolver: yupResolver(schema),
  });

  const { mutate: updateEvidence } = useApiEvidenceV1BulkUpdateSiteMaterialPartialUpdate({
    mutation: {
      onError() {
        open({
          message: t('evidence.editForm.notifications.updateError'),
          type: 'error',
        });
      },
      onSuccess() {
        open({
          message: t('evidence.editForm.notifications.updateSuccess'),
          type: 'success',
        });
        onSuccess?.();
      },
    },
  });
  const { deleteEvidence } = useBulkDeleteEvidence({
    mode: deleteMode,
    onError: () => {
      open({
        message: t('evidence.editForm.notifications.deleteError'),
        type: 'error',
      });
    },
    onSuccess: () => {
      open({
        message: t('evidence.editForm.notifications.deleteSuccess'),
        type: 'success',
      });
      onSuccess?.();
    },
    parentId: deleteParentId,
  });

  const handleDelete = () => {
    deleteEvidence({
      data: {
        evidence_ids: selectedEvidenceIds,
      },
    });
  };

  const handleEdit = (data: FormValues) => {
    updateEvidence({
      data: {
        evidence_ids: selectedEvidenceIds,
        evidence_type: data.evidence_type,
        parent_id: data.trade_company_site_material ? Number.parseInt(data.trade_company_site_material) : undefined,
      },
    });
  };

  return (
    <Form {...form}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-end',
          gap: 2,
        }}
      >
        <EvidenceTypeSelector
          label={t('evidence.editForm.labels.newEvidenceType', { values: { evidence: evidenceLabel } })}
          name="evidence_type"
          required
          getOptionDisabled={(option) => option.value === EvidenceTypeEnum.BP}
        />
        <TradeCompanySiteMaterialPicker />
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <ConfirmationButton
          disabled={selectedEvidenceIds.length === 0}
          variant="secondary"
          title={t('evidence.editForm.warnings.title')}
          message={t('evidence.editForm.warnings.deleteMessage')}
          onConfirm={handleDelete}
        >
          {t('evidence.editForm.actions.delete')} ({selectedEvidenceIds.length})
        </ConfirmationButton>

        <ConfirmationButton
          disabled={selectedEvidenceIds.length === 0}
          variant="primary"
          title={t('evidence.editForm.warnings.title')}
          message={t('evidence.editForm.warnings.editMessage')}
          onConfirm={form.handleSubmit(handleEdit)}
        >
          {t('evidence.editForm.actions.edit')} ({selectedEvidenceIds.length})
        </ConfirmationButton>
      </Stack>
    </Form>
  );
};
